/**
 * Deterministic attendance data generator.
 * Generates realistic mock attendance/punch data for ANY employee list.
 * Used as initialData / fallback so reports always show the logged-in
 * company's real employees — never hardcoded demo employees.
 *
 * "Deterministic" means: same employees + same month → identical output
 * every render, no randomness, no flickering.
 */

// Historical data ends here. Dates on/after this date come from real user actions.
const LIVE_CUTOFF = '2026-06-19';

// ─── Internal helpers ─────────────────────────────────────────────────────────

function isWeekend(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

function daysInMonth(year, mon) {
  return new Date(year, mon, 0).getDate(); // mon is 1-indexed
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function dateStr(year, mon, day) {
  return `${year}-${pad2(mon)}-${pad2(day)}`;
}

// Extract HH:MM from a punch_time string "YYYY-MM-DDTHH:MM:SS+05:30"
function toHHMM(iso) {
  if (!iso) return null;
  const m = iso.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : null;
}

// Safely coerce a department field (string OR object {id,name}) to a plain string
function deptString(dept) {
  if (!dept) return '';
  if (typeof dept === 'string') return dept;
  // Object shape: { id, name } — common from the employee service
  return String(dept.name || dept.label || dept.title || dept.department_name || '');
}

// Engineering-like departments get the 4-punch lunch-break pattern
function isEngineeringDept(dept) {
  const d = deptString(dept).toLowerCase();
  return d.includes('engineer') || d.includes('tech') || d.includes('product') || d.includes('design');
}

/**
 * For a given employee index, return a deterministic pattern:
 *   - isOT      : gets overtime (exits after 19:00)
 *   - is4Punch  : gets lunch break (4 punches instead of 2)
 *   - absentDays: set of day-numbers (1-based) that are absent
 *   - anomalyDay: day-number with single IN punch (no OUT)
 */
function getEmpPattern(empIndex, dept, totalDays) {
  const isOT     = empIndex % 4 === 0;                // every 4th employee gets OT
  const is4Punch = isEngineeringDept(dept) || empIndex % 6 === 1;

  // Spread 2 absent days evenly across employees using index
  const absentDay1 = 1 + (empIndex * 7  % totalDays);
  const absentDay2 = 1 + (empIndex * 13 % totalDays);
  const absentDays = new Set([absentDay1, absentDay2]);

  // One anomaly day per employee (IN only, no OUT)
  const anomalyDay = 1 + (empIndex * 17 % totalDays);
  // Anomaly day must not overlap absent days
  const safeAnomalyDay = absentDays.has(anomalyDay)
    ? 1 + ((anomalyDay + 1) % totalDays)
    : anomalyDay;

  // Deterministic "jitter" for check-in time: 0–15 min late
  const inJitter = (empIndex * 3) % 16; // 0..15 minutes

  return { isOT, is4Punch, absentDays, anomalyDay: safeAnomalyDay, inJitter };
}

/**
 * Build all punch records for one employee for one working day.
 * Returns array of punch objects.
 */
function buildDayPunches(empId, empCode, empName, dept, designation, deviceId, deviceName, date, pattern) {
  const { isOT, is4Punch, inJitter } = pattern;
  const punches = [];
  const base = `${date}T`;

  const inHour    = 9;
  const inMin     = inJitter;                    // 09:00 – 09:15
  const outHour   = isOT ? 19 : 18;
  const outMin    = isOT ? (5 + (inJitter % 10)) : (30 + (inJitter % 20)); // 19:05–19:14 or 18:30–18:49

  if (is4Punch) {
    // IN, lunch OUT, lunch IN, final OUT
    punches.push({ type: 'IN',  time: `${base}${pad2(inHour)}:${pad2(inMin)}:00+05:30`,  device: deviceId, deviceName });
    punches.push({ type: 'OUT', time: `${base}13:${pad2(5 + inJitter % 10)}:00+05:30`,  device: 'device-003', deviceName: 'Cafeteria' });
    punches.push({ type: 'IN',  time: `${base}14:${pad2(inJitter % 10)}:00+05:30`,       device: deviceId, deviceName });
    punches.push({ type: 'OUT', time: `${base}${pad2(outHour)}:${pad2(outMin)}:00+05:30`, device: deviceId, deviceName });
  } else {
    // Simple IN + OUT
    punches.push({ type: 'IN',  time: `${base}${pad2(inHour)}:${pad2(inMin)}:00+05:30`,  device: deviceId, deviceName });
    punches.push({ type: 'OUT', time: `${base}${pad2(outHour)}:${pad2(outMin)}:00+05:30`, device: deviceId, deviceName });
  }

  return punches.map((p, i) => ({
    id:            `gen-${empId}-${date}-${i}`,
    employee_id:   empId,
    employee_code: empCode,
    employee_name: empName,
    department:    dept || '',
    designation:   designation || '',
    device_id:     p.device,
    device_name:   p.deviceName,
    punch_type:    p.type,
    punch_time:    p.time,
    date,
    source:        'BIOMETRIC',
    status:        'PROCESSED',
    company_id:    'comp-001',
  }));
}

/**
 * Compute per-day attendance from punch records for monthly grid.
 */
function computeDayTimes(dayPunches) {
  if (!dayPunches.length) return null;

  const sorted   = [...dayPunches].sort((a, b) => a.punch_time.localeCompare(b.punch_time));
  const inPunch  = sorted.find(p => p.punch_type === 'IN');
  const outPunch = [...sorted].reverse().find(p => p.punch_type === 'OUT');

  let ot_min = 0;
  if (outPunch) {
    const [h, m] = outPunch.punch_time.slice(11, 16).split(':').map(Number);
    const totalMin = h * 60 + m;
    if (totalMin > 1080) ot_min = totalMin - 1080; // 18:00 = 1080
  }

  const sequence = sorted.map(p => `${toHHMM(p.punch_time)} ${p.punch_type}`);

  return {
    in:          toHHMM(inPunch?.punch_time),
    out:         toHHMM(outPunch?.punch_time),
    ot_min,
    punch_count: dayPunches.length,
    punches:     sequence,
  };
}

function formatOT(minutes) {
  if (!minutes || minutes <= 0) return '0h 0m';
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generate monthly attendance grid data for ANY list of employees.
 * Returns the same shape as GET /v1/attendance/monthly-all.
 *
 * @param {Array}  employees - array from useEmployees() API response
 * @param {string} month     - 'YYYY-MM'
 * @param {object} filters   - { department?, employee_id? }
 */
export function generateAttendanceForEmployees(employees, month, filters = {}) {
  if (!month || !employees?.length) return { success: true, data: [] };

  const [yearStr, monStr] = month.split('-');
  const year = parseInt(yearStr, 10);
  const mon  = parseInt(monStr, 10); // 1-indexed
  const days = daysInMonth(year, mon);

  const result = [];

  employees.forEach((emp, empIndex) => {
    // Normalise employee shape (API returns first_name/last_name, mock returns name)
    const empId   = emp.id || emp.employee_id || `gen-${empIndex}`;
    const empCode = emp.employee_code;
    const empName = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown';
    const dept    = deptString(emp.department);
    const desig   = deptString(emp.designation);

    // Apply filters
    if (filters.department  && dept  !== filters.department)  return;
    if (filters.employee_id && empId !== filters.employee_id) return;

    const deviceId   = isEngineeringDept(dept) ? 'device-002' : 'device-001';
    const deviceName = isEngineeringDept(dept) ? 'Engineering Floor' : 'Main Entrance';
    const pattern    = getEmpPattern(empIndex, dept, days);

    const dayStatus = {};
    const dayTimes  = {};
    let P = 0, A = 0, HP = 0, L = 0, H = 0, S = 0, OT_min = 0;

    for (let d = 1; d <= days; d++) {
      const ds  = dateStr(year, mon, d);
      const key = String(d);

      // Live data starts at LIVE_CUTOFF — don't generate for those dates
      if (ds >= LIVE_CUTOFF) continue;

      if (isWeekend(ds)) {
        dayStatus[key] = 'S';
        S++;
        continue;
      }

      if (pattern.absentDays.has(d)) {
        dayStatus[key] = 'A';
        A++;
        continue;
      }

      if (d === pattern.anomalyDay) {
        // Single IN punch only (missing exit)
        const inJitter = pattern.inJitter;
        dayStatus[key] = 'L';
        dayTimes[key]  = {
          in:          `09:${pad2(inJitter)}`,
          out:         null,
          ot_min:      0,
          punch_count: 1,
          punches:     [`09:${pad2(inJitter)} IN`],
        };
        L++;
        continue;
      }

      // Normal working day
      const punches = buildDayPunches(empId, empCode, empName, dept, desig, deviceId, deviceName, ds, pattern);
      dayStatus[key] = 'P';
      const dt = computeDayTimes(punches);
      if (dt) {
        dayTimes[key] = dt;
        OT_min += dt.ot_min;
      }
      P++;
    }

    result.push({
      employee_id:   empId,
      employee_code: empCode,
      name:          empName,
      department:    dept,
      designation:   desig,
      days:          dayStatus,
      times:         dayTimes,
      summary: { P, A, HP, L, H, S, OT: formatOT(OT_min) },
    });
  });

  return { success: true, data: result };
}

/**
 * Generate punch log records for ANY list of employees over a date range.
 * Returns an array of punch log objects (same shape as GET /v1/attendance/punch-logs).
 *
 * @param {Array}  employees - array from useEmployees() API response
 * @param {string} dateFrom  - 'YYYY-MM-DD'
 * @param {string} dateTo    - 'YYYY-MM-DD'
 */
export function generatePunchLogsForEmployees(employees, dateFrom, dateTo) {
  if (!employees?.length || !dateFrom || !dateTo) return [];

  const from = new Date(dateFrom + 'T00:00:00');
  const to   = new Date(dateTo   + 'T00:00:00');
  const logs = [];

  employees.forEach((emp, empIndex) => {
    const empId   = emp.id || emp.employee_id || `gen-${empIndex}`;
    const empCode = emp.employee_code;
    const empName = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown';
    const dept    = deptString(emp.department);
    const desig   = deptString(emp.designation);

    const deviceId   = isEngineeringDept(dept) ? 'device-002' : 'device-001';
    const deviceName = isEngineeringDept(dept) ? 'Engineering Floor' : 'Main Entrance';

    // Figure out total working days in range for pattern calculation
    let totalDays = 0;
    for (let cur = new Date(from); cur <= to; cur.setDate(cur.getDate() + 1)) {
      if (!isWeekend(cur.toISOString().slice(0, 10))) totalDays++;
    }
    if (totalDays === 0) return;

    const pattern = getEmpPattern(empIndex, dept, Math.max(totalDays, 28));

    // Walk every day in range
    for (let cur = new Date(from); cur <= to; cur = new Date(cur.getTime() + 86400000)) {
      const ds = cur.toISOString().slice(0, 10);
      // Live data starts at LIVE_CUTOFF — don't generate for those dates
      if (ds >= LIVE_CUTOFF) continue;
      if (isWeekend(ds)) continue;

      // Get day-of-month for pattern lookup
      const dayOfMonth = cur.getDate();

      if (pattern.absentDays.has(dayOfMonth)) continue;

      if (dayOfMonth === pattern.anomalyDay) {
        // Single IN only
        logs.push({
          id:            `gen-${empId}-${ds}-0`,
          employee_id:   empId,
          employee_code: empCode,
          employee_name: empName,
          department:    dept,
          designation:   desig,
          device_id:     deviceId,
          device_name:   deviceName,
          punch_type:    'IN',
          punch_time:    `${ds}T09:${pad2(pattern.inJitter)}:00+05:30`,
          date:          ds,
          source:        'BIOMETRIC',
          status:        'PROCESSED',
          company_id:    'comp-001',
        });
        continue;
      }

      const dayPunches = buildDayPunches(empId, empCode, empName, dept, desig, deviceId, deviceName, ds, pattern);
      logs.push(...dayPunches);
    }
  });

  // Sort by punch_time DESC (matches backend sort order)
  logs.sort((a, b) => b.punch_time.localeCompare(a.punch_time));
  return logs;
}
